import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Mail, Send, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import {
  useCreateContactsBulkEmail,
  useBulkEmailPreview,
  useTestBulkEmail,
  useSendBulkEmail,
  useBulkEmailStats,
} from '../hooks/useContactsBulkEmail';

interface ContactsBulkEmailModalProps {
  open: boolean;
  onClose: () => void;
  /** Ids of the currently-selected, currently-visible rows — resolved server-side
   *  against contactVisibilityFilter, so a stale/foreign id is simply dropped. */
  selectedIds: string[];
  onSent?: () => void;
}

type Step = 'compose' | 'preview' | 'sending' | 'done';

/**
 * Subject/Message composer for CR15's Contacts bulk email. Deliberately thin:
 * on submit it creates an ad-hoc campaign (src/api-contacts/bulk-email.js),
 * then drives the SAME preview/test/send steps the Campaigns admin screen
 * uses, scoped to the recipients selected here.
 */
export function ContactsBulkEmailModal({ open, onClose, selectedIds, onSent }: ContactsBulkEmailModalProps) {
  const [step, setStep] = useState<Step>('compose');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [campaignId, setCampaignId] = useState<string | null>(null);
  const [testTo, setTestTo] = useState('');
  const [eligibleCount, setEligibleCount] = useState(0);
  const [skipped, setSkipped] = useState({ noEmail: 0, suppressed: 0 });

  const create = useCreateContactsBulkEmail();
  const preview = useBulkEmailPreview(step === 'preview' || step === 'sending' || step === 'done' ? campaignId : null);
  const test = useTestBulkEmail(campaignId);
  const send = useSendBulkEmail(campaignId);
  const stats = useBulkEmailStats(campaignId, { poll: step === 'sending' });

  const reset = () => {
    setStep('compose');
    setSubject('');
    setMessage('');
    setCampaignId(null);
    setTestTo('');
    setEligibleCount(0);
    setSkipped({ noEmail: 0, suppressed: 0 });
  };

  const handleClose = () => {
    onClose();
    // Deferred so the modal doesn't visibly clear itself before the fade-out.
    setTimeout(reset, 200);
  };

  const handlePreview = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.error('Subject and message are both required.');
      return;
    }
    try {
      const bodyHtml = message.split('\n').map((line) => `<p>${line || '&nbsp;'}</p>`).join('');
      const result = await create.mutateAsync({ contactIds: selectedIds, subject: subject.trim(), bodyHtml });
      setCampaignId(result.campaignId);
      setEligibleCount(result.eligibleCount);
      setSkipped(result.skipped);
      setStep('preview');
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e?.response?.data?.error || 'Could not prepare the email.');
    }
  };

  const handleTest = async () => {
    if (!testTo.trim()) { toast.error('Enter an address to send the test to.'); return; }
    try {
      await test.mutateAsync(testTo.trim());
      toast.success(`Test sent to ${testTo.trim()}`);
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e?.response?.data?.error || 'Test send failed.');
    }
  };

  const handleSend = async () => {
    try {
      await send.mutateAsync();
      setStep('sending');
      onSent?.();
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e?.response?.data?.error || 'Could not start the send.');
    }
  };

  const campaignStatus = stats.data?.campaign.status;
  useEffect(() => {
    if (step === 'sending' && (campaignStatus === 'sent' || campaignStatus === 'failed')) {
      setStep('done');
    }
  }, [step, campaignStatus]);

  return (
    <Modal open={open} onClose={handleClose} size="lg" title={
      <span className="inline-flex items-center gap-2"><Mail className="w-5 h-5" /> Send Bulk Email</span>
    }>
      {step === 'compose' && (
        <div className="space-y-4">
          <p className="text-xs text-slate-500">
            Sending to <span className="font-bold text-slate-700">{selectedIds.length}</span> selected contact(s).
            Only those with a valid, non-suppressed email address will actually receive it — you'll see the
            eligible count before anything sends.
          </p>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500">Subject</label>
            <input
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. New product catalogue for 2026"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500">Message</label>
            <textarea
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm h-40 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Write your message…"
            />
            <p className="mt-1 text-data-xs text-slate-400">
              An unsubscribe link is added automatically — every bulk email must carry one.
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={handleClose}>Cancel</Button>
            <Button onClick={handlePreview} disabled={create.isPending}>
              {create.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Preview'}
            </Button>
          </div>
        </div>
      )}

      {step === 'preview' && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-2xl font-black text-slate-900">{eligibleCount}</div>
              <div className="text-data-xs font-bold uppercase text-slate-400">Will receive it</div>
            </div>
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-2xl font-black text-slate-400">{skipped.noEmail}</div>
              <div className="text-data-xs font-bold uppercase text-slate-400">No email</div>
            </div>
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-2xl font-black text-slate-400">{skipped.suppressed}</div>
              <div className="text-data-xs font-bold uppercase text-slate-400">Opted out</div>
            </div>
          </div>

          {preview.data && !preview.data.ok && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700">
              <div className="flex items-center gap-1.5 font-bold"><AlertTriangle className="w-4 h-4" /> Cannot send yet</div>
              <ul className="mt-1 list-disc pl-5">
                {preview.data.issues.map((issue) => <li key={issue}>{issue}</li>)}
              </ul>
            </div>
          )}

          {preview.data?.preview && (
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-xs font-bold text-slate-500">Subject: {preview.data.preview.subject}</div>
              <div
                data-document
                className="mt-2 max-h-48 overflow-y-auto rounded-lg bg-slate-50 p-3 text-xs text-slate-700"
                dangerouslySetInnerHTML={{ __html: preview.data.preview.html }}
              />
            </div>
          )}

          <div className="flex items-center gap-2">
            <input
              className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-xs"
              placeholder="Send a test copy to…"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
            />
            <Button variant="secondary" onClick={handleTest} disabled={test.isPending}>
              {test.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Test'}
            </Button>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={handleClose}>Cancel</Button>
            <Button onClick={handleSend} disabled={!preview.data?.ok || send.isPending}>
              {send.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <span className="inline-flex items-center gap-1.5"><Send className="w-4 h-4" /> Send to {eligibleCount} contacts</span>
              )}
            </Button>
          </div>
        </div>
      )}

      {(step === 'sending' || step === 'done') && (
        <div className="space-y-4 text-center py-6">
          {step === 'sending' ? (
            <Loader2 className="mx-auto w-8 h-8 animate-spin text-blue-600" />
          ) : (
            <CheckCircle2 className="mx-auto w-8 h-8 text-emerald-600" />
          )}
          <div className="text-sm font-bold text-slate-700">
            {step === 'sending' ? 'Sending…' : campaignStatus === 'failed' ? 'Send failed' : 'Sent'}
          </div>
          {stats.data && (
            <div className="grid grid-cols-3 gap-3 text-center max-w-sm mx-auto">
              <div className="rounded-xl border border-slate-200 p-3">
                <div className="text-xl font-black text-slate-900">{stats.data.recipients.sent || 0}</div>
                <div className="text-data-xs font-bold uppercase text-slate-400">Sent</div>
              </div>
              <div className="rounded-xl border border-slate-200 p-3">
                <div className="text-xl font-black text-slate-400">{stats.data.recipients.failed || 0}</div>
                <div className="text-data-xs font-bold uppercase text-slate-400">Failed</div>
              </div>
              <div className="rounded-xl border border-slate-200 p-3">
                <div className="text-xl font-black text-slate-400">{stats.data.recipients.bounced || 0}</div>
                <div className="text-data-xs font-bold uppercase text-slate-400">Bounced</div>
              </div>
            </div>
          )}
          {step === 'done' && (
            <div className="pt-2"><Button onClick={handleClose}>Close</Button></div>
          )}
        </div>
      )}
    </Modal>
  );
}
